import React, { useEffect, useRef, useState } from "react";
import { View, Text, StyleSheet, Pressable, Linking, Animated, Easing } from "react-native";
import { ChevronDown, Search } from "lucide-react";
import { MotiView, AnimatePresence } from "moti";
import { colors, spacing, radii, typographyStyles } from "../../../styles/design-system";
import type { DeepResearchResultProps } from "./types";
import { getSourceEmoji } from "./utils";

function formatDurationSeconds(seconds: number): string {
  if (seconds >= 60) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  return `${seconds}s`;
}

export function DeepResearchResult({ sources, durationSeconds }: DeepResearchResultProps) {
  const [showSources, setShowSources] = useState(false);
  const chevronRotation = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(chevronRotation, {
      toValue: showSources ? 1 : 0,
      duration: 180,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [chevronRotation, showSources]);

  const chevronRotate = chevronRotation.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "180deg"],
  });

  return (
    <View style={styles.container}>
      <Pressable
        style={({ pressed }) => [
          styles.summaryButton,
          showSources && styles.summaryButtonActive,
          pressed && styles.summaryButtonPressed,
        ]}
        onPress={() => setShowSources(!showSources)}
        accessibilityRole="button"
        accessibilityLabel={`Researched ${sources.length} sources in ${formatDurationSeconds(durationSeconds)}. ${showSources ? "Collapse" : "Expand"}`}>
        <View style={styles.summaryIcon}>
          <Search size={12} color={colors.brand[600]} />
        </View>
        <Text style={styles.summaryText}>
          Researched {sources.length} source{sources.length !== 1 ? "s" : ""} in{" "}
          {formatDurationSeconds(durationSeconds)}
        </Text>
        <Animated.View style={[styles.chevron, { transform: [{ rotate: chevronRotate }] }]}>
          <ChevronDown size={14} color={colors.grey[400]} />
        </Animated.View>
      </Pressable>

      <AnimatePresence>
        {showSources && (
          <MotiView
            from={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ type: "timing", duration: 200 }}
            style={styles.sourcesList}>
            {sources.map((source) => (
              <Pressable
                key={source.id}
                style={({ pressed }) => [styles.sourceRow, pressed && styles.sourceRowPressed]}
                onPress={() => source.url && Linking.openURL(source.url)}
                accessibilityRole="link"
                accessibilityLabel={source.title || source.url}>
                <Text style={styles.sourceEmoji}>{getSourceEmoji(source)}</Text>
                <View style={styles.sourceContent}>
                  <Text style={styles.sourceTitle}>{source.title || source.url}</Text>
                  {source.url && (
                    <Text style={styles.sourceUrl} numberOfLines={1}>
                      {source.url}
                    </Text>
                  )}
                </View>
              </Pressable>
            ))}
          </MotiView>
        )}
      </AnimatePresence>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: spacing.xs,
  },
  summaryButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.lg,
  },
  summaryButtonActive: {
    backgroundColor: colors.grey[100],
  },
  summaryButtonPressed: {
    backgroundColor: colors.grey[50],
  },
  summaryIcon: {
    width: 20,
    height: 20,
    borderRadius: radii.full,
    backgroundColor: colors.brand[50],
    alignItems: "center",
    justifyContent: "center",
  },
  summaryText: {
    ...typographyStyles.textSm,
    color: colors.grey[600],
    fontWeight: "500",
    flex: 1,
  },
  chevron: {
    marginLeft: "auto",
  },
  sourcesList: {
    paddingLeft: spacing.lg,
    paddingRight: spacing.sm,
    paddingVertical: spacing.sm,
    gap: spacing.xs,
  },
  sourceRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: spacing.xs,
  },
  sourceRowPressed: {
    opacity: 0.8,
  },
  sourceEmoji: {
    fontSize: 14,
    marginTop: 2,
  },
  sourceContent: {
    flex: 1,
  },
  sourceTitle: {
    ...typographyStyles.textSm,
    color: colors.grey[700],
  },
  sourceUrl: {
    ...typographyStyles.textXs,
    color: colors.grey[400],
    marginTop: 2,
  },
});
